import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { useEffect, useState } from 'react';
import { emptyServiceLocationDetails, ErrorMessages } from '@types';
import { useCycleStore } from '@cycle-hangars/store/store';
import * as Sentry from '@sentry/react';
import { FileObject, useUploadImage, useDeleteImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  partitionFileObjects,
  findDeletedImage,
  scrollTop,
} from '@utils';

const UploadPhotoTextContent = () => {
  return (
    <div className="text-bodyLg my-4 flex flex-col gap-4">
      <p>
        Photos help us locate and assess the issue.{' '}
        <a
          href="https://www.westminster.gov.uk/data-protection/report-it-image-disclaimer"
          className="text-action-link underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          Find out how we will be using your image.
        </a>
      </p>
      <p className="font-semibold">
        Only if it is safe to do so, please take a photo of the inside and
        outside of the cycle hangar.
      </p>
      <p>You can upload up to five images.</p>
      <p>You can upload JPEG, PNG, GIF or WebP files.</p>
    </div>
  );
};

export const UploadPhotoPage = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const {
    uploadedImages,
    uploadedImagesFileName,
    uploadedImagesFileType,
    uploadedImagesBlob,
    updateUploadedImages,
    serviceLocationDetails,
    updateIsPhotoLocationStored,
    fileWithLoadedCoordinates,
    updateFileWithLoadedCoordinates,
    updateServiceLocationDetails,
    updateUploadedImagesBlobs,
    updatePhotoCoordinates,
  } = useCycleStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  // use mutateAsync due to async operations after successful upload
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);
  const pageTitle = 'Upload a photo of the cycle hangar';

  useEffect(() => {
    // retrieve previously uploaded images from localStorage and display them on page load
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = async (files: File[]) => {
    if (!files || files.length === 0) {
      return;
    }
    setProcessingPhoto(true);
    const newFile = {
      file: files[files.length - 1],
      blob_endpoint: '',
      blob_uri: '',
    };
    const uploadedFileObjects =
      uploadedFiles.length > 0 ? [...uploadedFiles, newFile] : [newFile];
    setUploadedFiles(uploadedFileObjects);

    try {
      const partitionedFiles = await partitionFileObjects(uploadedFileObjects);
      updateUploadedImages(partitionedFiles);
      setErrorMessages([]);
    } catch (error) {
      Sentry.captureException(error);
      setErrorMessages([
        { path: 'upload-photo', message: (error as Error).message },
      ]);
    } finally {
      setProcessingPhoto(false);
    }
  };

  const handleDelete = (files: File[]) => {
    setProcessingPhoto(true);
    const deletedFileObject = findDeletedImage(uploadedFiles, files);
    let remainingFiles: FileObject[];
    if (deletedFileObject) {
      if (deletedFileObject.file.name === fileWithLoadedCoordinates) {
        updateServiceLocationDetails(emptyServiceLocationDetails);
        updatePhotoCoordinates(null);
        updateIsPhotoLocationStored(false);
        updateFileWithLoadedCoordinates('');
      }
      // if the photo has been uploaded to blob, delete it from the blob
      if (deletedFileObject.blob_endpoint) {
        remainingFiles = uploadedFiles.filter(
          (file) => file.blob_endpoint !== deletedFileObject.blob_endpoint,
        );
        try {
          mutateDelete(deletedFileObject.blob_endpoint);
          setUploadedFiles(remainingFiles);
          partitionFileObjects(remainingFiles).then((partitionedFiles) => {
            updateUploadedImages(partitionedFiles);
          });
          setErrorMessages([]);
        } catch (error) {
          Sentry.captureException(error);
          setErrorMessages([
            { path: 'upload-photo', message: (error as Error).message },
          ]);
        } finally {
          setProcessingPhoto(false);
        }
        // if not uploaded to the blob, delete from local storage
      } else {
        remainingFiles = uploadedFiles.filter(
          (file) => file.file.name !== deletedFileObject.file.name,
        );
        setUploadedFiles(remainingFiles);
        partitionFileObjects(remainingFiles).then((partitionedFiles) => {
          updateUploadedImages(partitionedFiles);
        });
        setErrorMessages([]);
      }
    } else {
      setErrorMessages([
        {
          path: 'upload-photo',
          message: 'Failed to delete image. Please try again',
        },
      ]);
    }
    setProcessingPhoto(false);
  };

  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };

  const buttonNext = {
    label: 'I do not have a photo',
    loading: isPending,
    disabled: processingPhoto,
    onClick: async () => {
      const { latitude, longitude } = serviceLocationDetails;
      if (latitude && longitude) {
        updatePhotoCoordinates({ latitude, longitude });
        updateIsPhotoLocationStored(true);
      }

      if (uploadedFiles.length === 0) {
        navigate(RoutesList.DETAILS);
        scrollTop();
      }
      let updatedImagesBlobsArray: typeof uploadedImagesBlob =
        uploadedImagesBlob.filter(
          (item) => !!item.blob_endpoint && !!item.blob_uri,
        );
      for (const fileObject of uploadedFiles) {
        const { file, blob_endpoint, blob_uri } = fileObject;
        // if the file has not been uploaded to the blob, upload it
        if (!blob_endpoint || !blob_uri) {
          await mutateImageUpload(file, {
            onSuccess: (data) => {
              const updatedFileObject = {
                filename: file.name,
                blob_endpoint: data.blob_endpoint,
                blob_uri: data.blob_uri,
              };
              updatedImagesBlobsArray.push(updatedFileObject);
            },
            onError: (error) => {
              Sentry.captureException(error);
              setErrorMessages([
                { path: 'upload-photo', message: error.message },
              ]);
            },
          });
        }
      }
      updateUploadedImagesBlobs(updatedImagesBlobsArray);
      navigate(RoutesList.DETAILS);
      scrollTop();
    },
  };

  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: () => {
      navigate(RoutesList.HOME);
      scrollTop();
    },
  };
  return (
    <UploadPhotoTemplate
      formTitle={pageTitle}
      description={<UploadPhotoTextContent />}
      handleFileChange={handleFileChange}
      handleUploadError={handleUploadError}
      handleDelete={handleDelete}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={5}
      errorPath={'upload-photo'}
      errorMessages={errorMessages}
      initialFiles={uploadedFiles.map((file) => file.file)}
    />
  );
};
